import { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import RootLayoutAdmin from "../components/admin/RootLayoutAdmin";
import HomeAdmin from "../pages/admin/HomeAdmin";
import { checkAuthLoader, redirectIfAuthenticatedLoader, tokenLoader } from "../util/admin/authAdmin";
import { action as logoutAction } from "../pages/admin/LogoutAdmin";
import FeatureGate from "../components/admin/FeatureGate.js";
import NotFound from "../pages/NotFound.js";
import { SEO_PATHS } from "../components/admin/landing/seoPaths";

// Alleen de landingspagina zit in de eerste download. Het dashboard (één gedeeld bestand),
// inloggen/registreren en de juridische pagina's laden pas wanneer iemand ze opent.
const LoginAdmin = lazy(() => import(/* webpackChunkName: "account" */ "../pages/admin/LoginAdmin"));
const SignupAdmin = lazy(() => import(/* webpackChunkName: "account" */ "../pages/admin/SignupAdmin"));
const TermsAndConditions = lazy(() => import(/* webpackChunkName: "legal" */ "../pages/TermsAndConditions.js"));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "legal" */ "../pages/PrivacyPolicy.js"));
const SeoLanding = lazy(() => import(/* webpackChunkName: "info" */ "../pages/admin/SeoLanding.js"));

const RootLayoutDashboardAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../components/admin/RootLayoutDashboardAdmin"));
const HomeDashboardAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/HomeDashboardAdmin.js"));
const ProductsAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/ProductsAdmin"));
const MenusAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/MenusAdmin.js"));
const SettingsAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/SettingsAdmin.js"));
const PickupMomentsAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/PickupMomentsAdmin.js"));
const OrdersAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/OrdersAdmin.js"));
const DiscountsAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/DiscountsAdmin.js"));
const CollectionsAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/CollectionsAdmin.js"));
const DeliveryZonesAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/DeliveryZonesAdmin.js"));
const GiftCardsAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/GiftCardsAdmin.js"));
const ReservationsAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/ReservationsAdmin.js"));
const SubscriptionAdmin = lazy(() => import(/* webpackChunkName: "dashboard" */ "../pages/admin/SubscriptionAdmin.js"));

const page = (element) => <Suspense fallback={null}>{element}</Suspense>;

export const adminRouter = createBrowserRouter([
   {
      path: "/",
      element: <RootLayoutAdmin />,
      children: [
         {
            index: true, // Default child voor "/"
            element: <HomeAdmin />,
            loader: redirectIfAuthenticatedLoader,
         },
         {
            path: "inloggen", // Nested route voor "/login"
            element: page(<LoginAdmin />),
         },
         {
            path: "registreren", // Nested route voor "/signup"
            element: page(<SignupAdmin />),
         },
         {
            path: "logout",
            action: logoutAction,
         },
         {
            path: "algemene-voorwaarden",
            element: page(<TermsAndConditions />),
         },
         {
            path: "privacy",
            element: page(<PrivacyPolicy />),
         },
         // Inhoudspagina's voor zoekmachines (bv. /online-bestellen-traiteur)
         ...SEO_PATHS.map((path) => ({
            path: path.slice(1),
            element: page(<SeoLanding path={path} />),
         })),
      ],
   },
   {
      path: "/dashboard",
      element: page(<RootLayoutDashboardAdmin />),
      loader: tokenLoader, // Controleert de tokenstatus voor deze routes
      children: [
         {
            index: true, // Default child voor "/"
            element: page(<HomeDashboardAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'producten',
            element: page(<ProductsAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'menus',
            element: page(<MenusAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'instellingen',
            element: page(<SettingsAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'ophaalmomenten',
            element: page(<PickupMomentsAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'bestellingen',
            element: page(<OrdersAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'reservaties',
            element: page(
               <FeatureGate feature="reservations">
                  <ReservationsAdmin />
               </FeatureGate>
            ),
            loader: checkAuthLoader
         },
         {
            path: 'kortingen',
            element: page(<DiscountsAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'collecties',
            element: page(<CollectionsAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'levering',
            element: page(<DeliveryZonesAdmin />),
            loader: checkAuthLoader
         },
         {
            path: 'cadeaubonnen',
            element: page(
               <FeatureGate feature="giftCards">
                  <GiftCardsAdmin />
               </FeatureGate>
            ),
            loader: checkAuthLoader
         },
         {
            path: 'abonnement',
            element: page(<SubscriptionAdmin />),
            loader: checkAuthLoader
         },
      ],
   },
   {
      path: "*",
      element: <NotFound />
   }
]);
